
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@supabase/auth-helpers-react';
import { Event } from '@/types/event';

// Extended event type to include guest-specific properties
export interface UserEvent extends Event {
  isGuest?: boolean;
  inviteId?: string;
  rsvpStatus?: 'invited' | 'accepted' | 'declined' | 'maybe';
  // Ensure these fields are explicitly included (even though they're in Event)
  event_venue_recommendations: any[]; // Match Event type
  event_date_recommendations: any[]; // Match Event type
}

export const useUserEvents = () => {
  const user = useUser();
  
  const { data: events, isLoading, error } = useQuery({
    queryKey: ['user-events', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      console.log('Fetching events for user:', user.id);
      
      // Fetch hosted events
      const { data: hostedEvents, error: hostedError } = await supabase
        .from('events')
        .select(`
          event_id,
          name,
          description,
          event_date,
          status,
          host_user_id,
          archived,
          image_url,
          location_type,
          flexible_location_description,
          preferred_dates,
          preferred_venues,
          event_deadline,
          created_at,
          updated_at,
          preferred_time_of_day,
          event_venue_recommendations(
            recommendation_id,
            is_selected,
            venue:venues(
              name,
              address,
              image_url,
              style,
              price_range
            )
          ),
          event_date_recommendations(
            recommendation_id,
            is_selected,
            recommended_date,
            time_of_day
          ),
          event_images(
            image_id,
            image_url,
            is_primary,
            display_order
          )
        `)
        .eq('host_user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (hostedError) {
        console.error('Error fetching hosted events:', hostedError);
        throw hostedError;
      }
      
      // Fetch events where user is invited
      const { data: invitedEvents, error: invitedError } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          rsvp_status,
          guest_avatar_url,
          event:events(
            event_id,
            name,
            description,
            event_date,
            status,
            host_user_id,
            archived,
            image_url,
            location_type,
            flexible_location_description,
            preferred_dates,
            preferred_venues,
            event_deadline,
            created_at,
            updated_at,
            preferred_time_of_day,
            event_venue_recommendations(
              recommendation_id,
              is_selected,
              venue:venues(
                name,
                address,
                image_url,
                style,
                price_range
              )
            ),
            event_date_recommendations(
              recommendation_id,
              is_selected,
              recommended_date,
              time_of_day
            ),
            event_images(
              image_id,
              image_url,
              is_primary,
              display_order
            )
          )
        `)
        .eq('claimed_by_user_id', user.id)
        .not('event', 'is', null)
        .order('created_at', { ascending: false });
      
      if (invitedError) {
        console.error('Error fetching invited events:', invitedError);
        throw invitedError;
      }
      
      console.log('Hosted events:', hostedEvents);
      console.log('Invited events:', invitedEvents);
      
      // Transform hosted events to UserEvent type
      const transformedHostedEvents: UserEvent[] = hostedEvents.map(event => ({
        ...event,
        isGuest: false,
        // Ensure these fields are explicitly matched
        event_venue_recommendations: event.event_venue_recommendations || [],
        event_date_recommendations: event.event_date_recommendations || []
      }));
      
      // Transform invited events to UserEvent type
      const transformedInvitedEvents: UserEvent[] = invitedEvents
        .filter(invite => invite.event) // Filter out any null events
        .map(invite => ({
          ...invite.event,
          isGuest: true,
          inviteId: invite.invite_id,
          rsvpStatus: invite.rsvp_status,
          guest_avatar_url: invite.guest_avatar_url, // Include guest_avatar_url
          // Ensure these fields are explicitly matched
          event_venue_recommendations: invite.event.event_venue_recommendations || [],
          event_date_recommendations: invite.event.event_date_recommendations || []
        }));
      
      // Combine both lists
      const allEvents: UserEvent[] = [
        ...transformedHostedEvents,
        ...transformedInvitedEvents
      ];
      
      console.log('Combined user events:', allEvents);
      return allEvents;
    },
    enabled: !!user
  });

  return {
    events,
    isLoading,
    error
  };
};
